import {
  DOCUMENT,
  Injectable,
  NgZone,
  OnDestroy,
  computed,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LocalStorageFacade } from '@application/localstorage';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { DateTime } from 'luxon';
import { toReleasableRefusal } from '../entities/blocked-attempt.util';
import {
  AccessPoint,
  HubConnectionStatus,
} from '../entities/access-edges.interface';
import {
  AccessPointEdgeEventLogEvent,
  AccessPointEdgeHubService,
} from './access-point-edge-hub.service';
import {
  DOCUMENT_PICTURE_IN_PICTURE,
  IReleasableRefusal,
  MINI_MONITOR_CHANNEL,
  REFUSAL_ALERT_SOUND,
  TDetachedSlot,
  TDetachedWindowLayout,
  TMiniMonitorMessage,
} from './edge-control.tokens';

/**
 * Visibilidade do mini-monitor (o widget de pontos de acesso) e ciclo de vida
 * do mini-monitor destacado.
 *
 * Destacar leva o mini-monitor para uma janela Document Picture-in-Picture, que
 * fica por cima de todas as janelas do computador. Não é uma nova instância da
 * aplicação: os elementos registados em `registerDetachedContent` **mudam de
 * documento** — os mesmos componentes, os mesmos stores, a mesma conexão — e
 * voltam ao lugar de onde saíram quando a janela fecha. Vestir a janela — o
 * visual da página e os lugares — é apresentação e não mora aqui: chega com o
 * pedido de destaque (`detach(layout)`), vindo da feature.
 */
@Injectable({ providedIn: 'root' })
export class EdgeControlService implements OnDestroy {
  /** Largura do mini-monitor na página; a janela destacada abre com ela. */
  private static readonly DETACHED_WIDTH = 400;
  /** Altura para o mini-monitor e alguns itens da Live Access Activity abaixo dele. */
  private static readonly DETACHED_HEIGHT = 640;

  /**
   * "Estava destacado" é estado da estação, guardado por navegador como o resto
   * do mini-monitor (posição, visibilidade, ponto de acesso, recolhido). Chave
   * irmã de `edge-control-state` e não um campo dentro dele: o componente
   * regrava aquele objeto inteiro a cada mudança e apagaria a marca.
   */
  private static readonly DETACHED_STATE_KEY = 'edge-control-detached';

  /**
   * Quanto uma aba nova espera que a dona da janela destacada responda antes de
   * concluir que ela já não existe e mostrar o aviso. A dona responde de imediato
   * mesmo em segundo plano: mensagem entre abas não é temporizador.
   */
  private static readonly OWNER_REPLY_MS = 300;

  /** Tentativas já anunciadas: o evento pode chegar duas vezes, ou de novo depois de reconectar. */
  private static readonly ANNOUNCED_ATTEMPTS_KEPT = 200;

  private readonly pictureInPicture = inject(DOCUMENT_PICTURE_IN_PICTURE);
  private readonly mainDocument = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly hub = inject(AccessPointEdgeHubService);
  private readonly storage = inject(LocalStorageFacade);
  private readonly channel = inject(MINI_MONITOR_CHANNEL)();
  private readonly playRefusalAlert = inject(REFUSAL_ALERT_SOUND);
  private readonly announcedAttempts = new Set<string>();
  private readonly releasableRefusals = new Subject<IReleasableRefusal>();

  /**
   * Cada recusa que ainda pode ser liberada, uma vez por tentativa. É dado, não
   * aviso: sai com o mini-monitor destacado ou não, com o som ligado ou não.
   * Quem ouve decide se avisa — o som do destacado, aqui dentro, e a notificação
   * do sistema, na Live Access Activity.
   */
  readonly releasableRefusal$ = this.releasableRefusals.asObservable();

  // Antes do hub: o som já ouve quando a primeira recusa chegar.
  private readonly refusalSoundSubscription = this.releasableRefusal$.subscribe(
    () => this.playRefusalSound()
  );
  // O hub emite fora da zona do Angular quando o evento chega pelo WebSocket.
  private readonly edgeEventSubscription = this.hub.edgeEventLog$.subscribe(
    (event) => this.ngZone.run(() => this.announceRefusal(event))
  );

  /** Esta aba nesta carga da página: é ela que fica dona da janela destacada. */
  private readonly tabId = uuid();
  /** A página começou a ir-se embora (recarga, saída do sistema, troca de conta). */
  private unloading = false;
  private pendingCloseDecision: ReturnType<typeof setTimeout> | null = null;
  private ownerReplyTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Ponto de acesso do mini-monitor desta aba, partilhado quando ela é a dona. */
  private sharedAccessPointId: string | null = null;
  /**
   * Ponto de acesso escolhido nesta aba enquanto ela ainda pergunta se há dona:
   * vai para a dona se ela responder, ou fica aqui se ninguém responder.
   */
  private choiceAwaitingOwner: AccessPoint | null = null;

  private readonly hubStatus = toSignal(this.hub.hubStatus$, {
    initialValue: 'disconnected' as HubConnectionStatus,
  });

  private readonly _isWidgetVisible = signal<boolean>(false);
  private readonly _targetAccessPoint = signal<AccessPoint | null>(null);
  private readonly _detachedWindow = signal<Window | null>(null);

  /** A estação guarda um mini-monitor destacado que a recepção não fechou. */
  private readonly _wasDetached = signal(this.detachedOwner() !== null);
  /** A dona da janela destacada, quando é outra aba — e o ponto de acesso dela. */
  private readonly _ownerElsewhere = signal<{
    owner: string;
    accessPointId: string | null;
  } | null>(null);
  /** Aba nova com a marca da estação, a perguntar se a dona ainda vive. */
  private readonly _awaitingOwner = signal(false);
  /**
   * A preferência do som da recusa, como o componente do mini-monitor a entregou.
   * Desligada até alguém dizer que sim.
   */
  private readonly _refusalSoundEnabled = signal(false);
  /** Esta aba está à frente da recepção: visível e com o foco do computador. */
  private readonly _inFront = signal(false);
  /** A outra aba da estação que está à frente, quando há. */
  private readonly _inFrontElsewhere = signal<string | null>(null);

  readonly isWidgetVisible = this._isWidgetVisible.asReadonly();

  /**
   * "O mini-monitor destacado foi fechado — Destacar de novo": a janela fechou
   * sem a recepção pedir. O navegador só a reabre com um clique.
   */
  readonly reattachNotice = computed(
    () => this._wasDetached() && !this.isDetached() && !this.shownInAnotherTab()
  );
  readonly targetAccessPoint = this._targetAccessPoint.asReadonly();

  /** O mini-monitor está numa janela destacada aberta por esta aba. */
  readonly isDetached = computed(() => this._detachedWindow() !== null);

  /**
   * Sem conexão com os pontos de acesso — a reconectar ou desligada. O mini-monitor
   * destacado fica aberto o dia todo e, sem aviso, "nenhuma recusa nos últimos 10
   * minutos" e "sem conexão há 10 minutos" ficam com a mesma cara.
   */
  readonly connectionLost = computed(() => {
    const status = this.hubStatus();
    return status === 'reconnecting' || status === 'disconnected';
  });

  /** O mini-monitor destacado está aberto noutra aba da estação. */
  readonly detachedElsewhere = computed(() => this._ownerElsewhere() !== null);

  /** Ponto de acesso do mini-monitor destacado noutra aba: F2/F3 aqui liberam esse. */
  readonly detachedElsewhereAccessPointId = computed(
    () => this._ownerElsewhere()?.accessPointId ?? null
  );

  /**
   * Esconde o mini-monitor da página: ele está destacado noutra aba, ou esta aba
   * ainda pergunta se está. Assim uma aba nova (o perfil aberto do mini-monitor)
   * não mostra um segundo mini-monitor nem um aviso falso.
   */
  readonly shownInAnotherTab = computed(
    () => this.detachedElsewhere() || this._awaitingOwner()
  );

  /**
   * O botão Destacar só existe onde a API existe (Chrome e Edge de computador), e
   * nunca enquanto outra aba tem a janela: uma janela destacada por estação.
   */
  readonly canDetach = computed(
    () => this.pictureInPicture !== null && !this.shownInAnotherTab()
  );

  /**
   * Uma aba do sistema nesta estação está à frente da recepção — esta ou outra:
   * visível e com o foco do computador. A recepção já está a olhar para o
   * sistema, e a notificação da recusa sobraria. Visível sem foco não conta: o
   * navegador atrás de outro programa, meio à vista, é justamente o caso da
   * notificação. O foco é de uma janela só no computador inteiro, por isso há no
   * máximo uma aba à frente.
   */
  readonly tabInFront = computed(
    () => this._inFront() || this._inFrontElsewhere() !== null
  );

  /** Elementos que vão para a janela destacada, por lugar. */
  private readonly detachedContent = new Map<TDetachedSlot, HTMLElement>();
  /** Marcador deixado na página no lugar de cada elemento levado para a janela. */
  private readonly returnMarkers = new Map<HTMLElement, Comment>();
  private detachedSlots: ReadonlyMap<TDetachedSlot, HTMLElement> | null = null;
  /** Desfaz o que o layout deixou a correr na janela destacada. */
  private disposeLayout: (() => void) | null = null;

  openWidget(): void {
    this.choiceAwaitingOwner = null;
    this._targetAccessPoint.set(null);
    this._isWidgetVisible.set(true);
  }

  /**
   * Abre o mini-monitor no ponto de acesso escolhido (seletor da barra, menu da
   * pessoa, busca). Destacado noutra aba, a escolha vai para a dona: é ela que
   * troca o mini-monitor destacado, e esta aba segue-a quando ela partilhar o
   * ponto novo. Escolher aqui por conta própria deixaria F2/F3 a liberar um
   * ponto de acesso que a janela destacada não mostra — e o mini-monitor desta
   * aba, escondido, não o diria a ninguém.
   *
   * Enquanto esta aba ainda pergunta se há dona, a escolha espera pela resposta,
   * pelo mesmo motivo: nunca muda o ponto de acesso daqui antes da dona.
   */
  openWidgetWithAccessPoint(accessPoint: AccessPoint): void {
    this._isWidgetVisible.set(true);
    const ownerElsewhere = this._ownerElsewhere();
    if (ownerElsewhere) {
      this.post({
        type: 'choose-access-point',
        owner: ownerElsewhere.owner,
        accessPoint,
      });
      return;
    }
    if (this._awaitingOwner()) {
      this.choiceAwaitingOwner = accessPoint;
      return;
    }
    this._targetAccessPoint.set(accessPoint);
  }

  constructor() {
    this.mainDocument.defaultView?.addEventListener(
      'pagehide',
      this.onPageHide
    );
    this.channel?.addEventListener('message', this.onMessage);

    this.mainDocument.addEventListener('visibilitychange', this.onFrontChange);
    this.mainDocument.defaultView?.addEventListener('focus', this.onFrontChange);
    this.mainDocument.defaultView?.addEventListener('blur', this.onFrontChange);
    this.updateInFront();
    // Uma aba aberta atrás (o perfil) não sabe qual está à frente: pergunta.
    this.post({ type: 'who-is-in-front' });

    // A marca da estação pode ser de um destacado ainda aberto noutra aba:
    // pergunta antes de avisar.
    if (this._wasDetached() && this.channel) {
      this._awaitingOwner.set(true);
      this.post({ type: 'who-owns' });
      this.ownerReplyTimeout = setTimeout(() => {
        this.ownerReplyTimeout = null;
        this.ngZone.run(() => {
          this._awaitingOwner.set(false);
          // Ninguém respondeu: a escolha feita entretanto é desta aba.
          const choice = this.choiceAwaitingOwner;
          this.choiceAwaitingOwner = null;
          if (choice) this._targetAccessPoint.set(choice);
        });
      }, EdgeControlService.OWNER_REPLY_MS);
    }
  }

  /**
   * O ponto de acesso que o mini-monitor desta aba mostra. Destacado, as outras
   * abas seguem-no, para F2/F3 lá liberarem o mesmo.
   */
  shareAccessPoint(accessPointId: string | null): void {
    this.sharedAccessPointId = accessPointId;
    if (this.isDetached()) {
      this.post({ type: 'access-point', owner: this.tabId, accessPointId });
    }
  }

  /**
   * Liga ou desliga o som da recusa. A preferência é do usuário e chega pelo
   * componente do mini-monitor: o serviço não lê preferências (Service > Store >
   * Component). Só um `true` explícito liga.
   */
  setRefusalSoundEnabled(enabled: boolean): void {
    this._refusalSoundEnabled.set(enabled === true);
  }

  closeWidget(): void {
    this._detachedWindow()?.close();
    this.forgetDetached();
    this._isWidgetVisible.set(false);
    this.choiceAwaitingOwner = null;
    this._targetAccessPoint.set(null);
  }

  clearTargetAccessPoint(): void {
    this._targetAccessPoint.set(null);
  }

  /**
   * Abre a janela destacada, veste-a com o `layout` e leva para os lugares dele
   * o conteúdo registado.
   *
   * Tem de ser chamado direto do clique: o navegador só abre a janela em
   * resposta a um gesto do usuário, e o pedido é a primeira coisa assíncrona
   * que acontece aqui. Rejeita se o navegador recusar.
   */
  async detach(layout: TDetachedWindowLayout): Promise<void> {
    if (!this.pictureInPicture || this._detachedWindow()) return;

    const detachedWindow = await this.pictureInPicture.requestWindow({
      width: EdgeControlService.DETACHED_WIDTH,
      height: EdgeControlService.DETACHED_HEIGHT,
    });
    this.ngZone.run(() => this.adoptDetachedWindow(detachedWindow, layout));
  }

  /**
   * Regista um elemento para ir para um lugar da janela destacada. Se ela já
   * estiver aberta, vai já. Devolve a função que desfaz o registo (e devolve o
   * elemento à página), para chamar ao destruir o componente.
   */
  registerDetachedContent(
    slot: TDetachedSlot,
    element: HTMLElement
  ): () => void {
    this.detachedContent.set(slot, element);
    const target = this.detachedSlots?.get(slot);
    if (target) this.moveInto(element, target);

    return () => {
      if (this.detachedContent.get(slot) === element)
        this.detachedContent.delete(slot);
      this.returnToPage(element);
    };
  }

  ngOnDestroy(): void {
    // O serviço só morre com a aplicação: o fecho da janela que vem a seguir é
    // da página, não da recepção, e a marca da estação fica.
    this.unloading = true;
    this.mainDocument.defaultView?.removeEventListener(
      'pagehide',
      this.onPageHide
    );
    this.mainDocument.removeEventListener('visibilitychange', this.onFrontChange);
    this.mainDocument.defaultView?.removeEventListener('focus', this.onFrontChange);
    this.mainDocument.defaultView?.removeEventListener('blur', this.onFrontChange);
    if (this.pendingCloseDecision) clearTimeout(this.pendingCloseDecision);
    if (this.ownerReplyTimeout) clearTimeout(this.ownerReplyTimeout);
    this._detachedWindow()?.close();
    this.channel?.removeEventListener('message', this.onMessage);
    this.channel?.close();
    this.edgeEventSubscription.unsubscribe();
    this.refusalSoundSubscription.unsubscribe();
    this.releasableRefusals.complete();
  }

  /**
   * Anuncia a recusa que ainda pode ser liberada em `releasableRefusal$`, uma vez
   * por tentativa: o evento pode chegar duas vezes, ou de novo depois de
   * reconectar. A tentativa conta como anunciada venha o aviso ou não — uma
   * recusa que chegou com o som desligado não toca se voltar depois de o ligar.
   */
  private announceRefusal(event: AccessPointEdgeEventLogEvent): void {
    const refusal = toReleasableRefusal(event, DateTime.now().toMillis());
    if (!refusal || this.announcedAttempts.has(refusal.attemptId)) return;

    this.announcedAttempts.add(refusal.attemptId);
    if (
      this.announcedAttempts.size > EdgeControlService.ANNOUNCED_ATTEMPTS_KEPT
    ) {
      const oldest = this.announcedAttempts.values().next().value;
      if (oldest) this.announcedAttempts.delete(oldest);
    }
    this.releasableRefusals.next(refusal);
  }

  /**
   * O som da recusa que ainda pode ser liberada. Toca só quando as três coisas
   * valem ao mesmo tempo: o mini-monitor está destacado; esta é a aba dona (a
   * que tem a janela — as outras também recebem o evento, e o bipe é um por
   * recusa); e a recusa tem identificador de tentativa e prazo em curso (só
   * essas chegam aqui). Mais a preferência do usuário, que o componente entrega
   * por `setRefusalSoundEnabled` e que começa desligada. Passagens permitidas e
   * recusas que já não se podem liberar ficam em silêncio: o som quer dizer "há
   * algo a fazer agora".
   */
  private playRefusalSound(): void {
    if (this.isDetached() && this._refusalSoundEnabled()) {
      this.playRefusalAlert();
    }
  }

  private readonly onPageHide = (): void => {
    this.unloading = true;
    // A dona vai-se embora: as outras abas passam a avisar. Vale também com a
    // janela já fechada mas a marca ainda desta aba — quem fechou ainda não
    // tinha sido decidido, e sem isto as outras ficavam escondidas para sempre.
    if (this.isDetached() || this.detachedOwner() === this.tabId) {
      this.post({ type: 'owner-gone', owner: this.tabId });
    }
    if (this._inFront()) this.post({ type: 'left-front', tab: this.tabId });
  };

  // `focus`/`blur` podem ser disparados de forma síncrona dentro de um consumer
  // reativo (render, `computed`); `untracked` limpa o `activeConsumer` para que
  // a escrita em `_inFront` não rebente com NG0600 (Sentry 7763277380).
  private readonly onFrontChange = (): void =>
    untracked(() => this.updateInFront());

  /** Relê se esta aba está à frente e, quando muda, diz às outras. */
  private updateInFront(): void {
    const inFront =
      this.mainDocument.visibilityState === 'visible' &&
      this.mainDocument.hasFocus();
    if (inFront === this._inFront()) return;
    this._inFront.set(inFront);
    // O foco é de uma janela só: a que estava à frente já não está, mesmo que
    // tenha ido embora sem avisar.
    if (inFront) this._inFrontElsewhere.set(null);
    this.post({ type: inFront ? 'in-front' : 'left-front', tab: this.tabId });
  }

  // O canal entrega fora da zona do Angular. `untracked` porque `handleMessage`
  // escreve signals e um consumer reativo ainda ativo (vazado de um tick que
  // lançou) faria a escrita rebentar com NG0600 (Sentry 7791882621).
  private readonly onMessage = (
    event: MessageEvent<TMiniMonitorMessage>
  ): void =>
    this.ngZone.run(() => untracked(() => this.handleMessage(event.data)));

  private handleMessage(message: TMiniMonitorMessage): void {
    switch (message.type) {
      case 'who-owns':
        if (this.isDetached()) {
          this.post({
            type: 'owner',
            owner: this.tabId,
            accessPointId: this.sharedAccessPointId,
          });
        }
        return;
      case 'owner':
      case 'detached':
      case 'access-point':
        if (this.isDetached()) return;
        this._ownerElsewhere.set({
          owner: message.owner,
          accessPointId: message.accessPointId,
        });
        this._awaitingOwner.set(false);
        this._wasDetached.set(true);
        // F2/F3 respondem também aqui, com o mini-monitor escondido.
        this._isWidgetVisible.set(true);
        // A escolha feita enquanto esta aba perguntava vai agora para a dona.
        if (this.choiceAwaitingOwner) {
          this.post({
            type: 'choose-access-point',
            owner: message.owner,
            accessPoint: this.choiceAwaitingOwner,
          });
          this.choiceAwaitingOwner = null;
        }
        return;
      case 'choose-access-point':
        // Só a dona, com a janela aberta, troca o mini-monitor destacado — como
        // se a escolha tivesse sido feita aqui. O `access-point` que ela manda
        // a seguir leva as outras abas atrás.
        if (message.owner === this.tabId && this.isDetached()) {
          this.openWidgetWithAccessPoint(message.accessPoint);
        }
        return;
      case 'returned':
      case 'owner-gone':
        if (this._ownerElsewhere()?.owner === message.owner) {
          this._ownerElsewhere.set(null);
        }
        // Fechado pela recepção: nada de aviso. Pela página: a marca fica.
        if (message.type === 'returned') this._wasDetached.set(false);
        return;
      case 'who-is-in-front':
        if (this._inFront()) this.post({ type: 'in-front', tab: this.tabId });
        return;
      case 'in-front':
        this._inFrontElsewhere.set(message.tab);
        return;
      case 'left-front':
        if (this._inFrontElsewhere() === message.tab) {
          this._inFrontElsewhere.set(null);
        }
        return;
    }
  }

  private post(message: TMiniMonitorMessage): void {
    this.channel?.postMessage(message);
  }

  private detachedOwner(): string | null {
    return this.storage.getItem(EdgeControlService.DETACHED_STATE_KEY);
  }

  /**
   * A recepção decidiu: nada de aviso depois, nem nesta nem noutra carga. A marca
   * só sai se for desta aba, ou se não houver dona viva — nunca a de outra aba
   * com a janela aberta.
   */
  private forgetDetached(): void {
    const owner = this.detachedOwner();
    if (owner !== null && (owner === this.tabId || !this.detachedElsewhere())) {
      this.storage.removeItem(EdgeControlService.DETACHED_STATE_KEY);
      this.post({ type: 'returned', owner });
    }
    this._wasDetached.set(false);
  }

  private adoptDetachedWindow(
    detachedWindow: Window,
    layout: TDetachedWindowLayout
  ): void {
    // Veste a janela antes de o conteúdo entrar.
    const { slots, dispose } = layout(detachedWindow.document);
    this.disposeLayout = dispose;
    this.detachedSlots = slots;
    this.detachedContent.forEach((element, slot) => {
      const target = this.detachedSlots?.get(slot);
      if (target) this.moveInto(element, target);
    });

    // O `pagehide` da janela destacada corre fora da zona do Angular: o zone.js
    // só corrige a janela onde carregou.
    detachedWindow.addEventListener(
      'pagehide',
      () => this.ngZone.run(() => this.onDetachedWindowClosed(detachedWindow)),
      { once: true }
    );

    this._detachedWindow.set(detachedWindow);
    this._isWidgetVisible.set(true);
    this.storage.setItem(EdgeControlService.DETACHED_STATE_KEY, this.tabId);
    this._wasDetached.set(true);
    this._ownerElsewhere.set(null);
    this.post({
      type: 'detached',
      owner: this.tabId,
      accessPointId: this.sharedAccessPointId,
    });
  }

  private onDetachedWindowClosed(detachedWindow: Window): void {
    if (this._detachedWindow() !== detachedWindow) return;

    this.detachedContent.forEach((element) => this.returnToPage(element));
    this.disposeLayout?.();
    this.disposeLayout = null;
    this.detachedSlots = null;
    this._detachedWindow.set(null);

    // Quem fechou? Se foi a página (recarga, saída, troca de conta), ela está a
    // ir-se embora: o `pagehide` dela já chegou, ou esta tarefa nem corre. Só
    // um fecho da recepção chega aqui com a página viva — e limpa a marca.
    this.pendingCloseDecision = setTimeout(() => {
      this.pendingCloseDecision = null;
      if (!this.unloading) this.forgetDetached();
    });
  }

  private moveInto(element: HTMLElement, target: HTMLElement): void {
    if (!this.returnMarkers.has(element)) {
      const marker = this.mainDocument.createComment('mini-monitor destacado');
      element.parentNode?.insertBefore(marker, element);
      this.returnMarkers.set(element, marker);
    }
    target.appendChild(element);
  }

  private returnToPage(element: HTMLElement): void {
    const marker = this.returnMarkers.get(element);
    if (!marker) return;
    marker.parentNode?.insertBefore(element, marker);
    marker.remove();
    this.returnMarkers.delete(element);
  }
}
